import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import {
  Text,
  Group,
  Badge,
  Button,
  Table,
  ActionIcon,
  Modal,
  Select,
  Textarea,
  Stack,
  Center,
  Paper,
  Alert,
  Loader,
  UnstyledButton,
} from "@mantine/core";
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconUsers,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import RemoteSelect2 from "./remoteSelect2";
import {
  DATA_RELATIONSHIP,
  DATA_RELATIONSHIP_CORPORATE,
} from "../data/options";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { getEntityTabLabel } from "../utils/tabNavigation";

// Format relationship options for Mantine Select with groups
const formatOptions = (options) => {
  return options.map((opt) => ({
    value: opt.value,
    label: opt.label,
    group: opt.group,
  }));
};

// Get badge color based on relationship group
const getBadgeColor = (relationshipType, options) => {
  const option = options.find((opt) => opt.value === relationshipType);
  if (!option) return "gray";

  const groupColors = {
    // Personal relationship colors
    Spouse: "pink",
    Parent: "blue",
    Child: "green",
    Sibling: "orange",
    Extended: "grape",
    Other: "gray",
    // Corporate relationship colors
    Ownership: "blue",
    Partnership: "green",
    Branch: "cyan",
  };

  return groupColors[option.group] || "gray";
};

const ClientRelationshipList = ({
  form,
  role = "OPERATION",
  clientIDField = "_id",
  ...props
}) => {
  const clientId = _.get(form?.values, clientIDField);
  const clientType = _.get(form?.values, "type") || "INDIVIDUAL";
  const tabNav = useTabNavigate();

  const [api] = useServerApi();
  const { t } = useTranslation();

  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingRelationship, setDeletingRelationship] = useState(null);

  // Form state for add/edit modal
  const [formData, setFormData] = useState({
    relatedClient: null,
    relationshipType: "",
    remark: "",
  });

  const [submitting, setSubmitting] = useState(false);

  // Get relationship options based on client type
  const relationshipOptions =
    clientType === "CORPORATE"
      ? DATA_RELATIONSHIP_CORPORATE
      : DATA_RELATIONSHIP;

  const canEdit = role === "OPERATION";

  // Fetch relationships
  const fetchRelationships = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const result = await api.ClientRelationship.getByClient(clientId);
      if (result && result.relationships) {
        setRelationships(result.relationships);
      }
    } catch (error) {
      console.error("Error fetching relationships:", error);
      showNotification({
        title: t("Error"),
        message: t("Failed to load relationships"),
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [clientId, api, t]);

  useEffect(() => {
    fetchRelationships();
  }, [clientId]);

  // Open add modal
  const handleAdd = () => {
    setEditingRelationship(null);
    setFormData({
      relatedClient: null,
      relationshipType: "",
      remark: "",
    });
    setModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (relationship) => {
    setEditingRelationship(relationship);
    setFormData({
      relatedClient: relationship.relatedClient?._id,
      relationshipType: relationship.isInverse
        ? relationship.originalRelationshipType
        : relationship.relationshipType,
      remark: relationship.remark || "",
    });
    setModalOpen(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (relationship) => {
    setDeletingRelationship(relationship);
    setDeleteConfirmOpen(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.relatedClient || !formData.relationshipType) {
      showNotification({
        title: t("Validation Error"),
        message: t("Please select a client and relationship type"),
        color: "red",
      });
      return;
    }

    setSubmitting(true);
    try {
      if (editingRelationship) {
        // Update existing relationship
        const result = await api.ClientRelationship.update(
          editingRelationship.originalRelationshipId || editingRelationship._id,
          {
            relationshipType: formData.relationshipType,
            remark: formData.remark,
          },
        );

        if (result.success) {
          showNotification({
            title: t("Success"),
            message: t("Relationship updated successfully"),
            color: "green",
          });
        } else {
          throw new Error(result.message || "Update failed");
        }
      } else {
        // Create new relationship
        const result = await api.ClientRelationship.create({
          client: clientId,
          relatedClient: formData.relatedClient,
          relationshipType: formData.relationshipType,
          remark: formData.remark,
        });

        if (result.success) {
          showNotification({
            title: t("Success"),
            message: t("Relationship created successfully"),
            color: "green",
          });
        } else {
          throw new Error(result.message || "Create failed");
        }
      }

      setModalOpen(false);
      fetchRelationships();
    } catch (error) {
      console.error("Error saving relationship:", error);
      showNotification({
        title: t("Error"),
        message: error.message || t("Failed to save relationship"),
        color: "red",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deletingRelationship) return;

    setSubmitting(true);
    try {
      const result = await api.ClientRelationship.delete(
        deletingRelationship.originalRelationshipId || deletingRelationship._id,
      );

      if (result.success) {
        showNotification({
          title: t("Success"),
          message: t("Relationship deleted successfully"),
          color: "green",
        });
        setDeleteConfirmOpen(false);
        setDeletingRelationship(null);
        fetchRelationships();
      } else {
        throw new Error(result.message || "Delete failed");
      }
    } catch (error) {
      console.error("Error deleting relationship:", error);
      showNotification({
        title: t("Error"),
        message: error.message || t("Failed to delete relationship"),
        color: "red",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Navigate to related client - use current URL path, just change the id
  const handleClientClick = (relatedClient) => {
    if (relatedClient?._id) {
      const currentPath = window.location.pathname;
      tabNav.openDetail({
        pathname: currentPath,
        id: relatedClient._id,
        mode: "edit",
        label: getEntityTabLabel(relatedClient, relatedClient._id, "client"),
      });
    }
  };

  // Handle related client selection
  const handleRelatedClientChange = (selectedData, _form) => {
    if (selectedData) {
      setFormData((prev) => ({
        ...prev,
        relatedClient: selectedData._id,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        relatedClient: null,
      }));
    }
  };

  // Render empty state
  const renderEmptyState = () => (
    <Center py="xl">
      <Stack align="center" spacing="md">
        <IconUsers size={48} color="gray" />
        <Text color="dimmed" size="sm">
          {t("No relationships have been added yet")}
        </Text>
        {canEdit && (
          <Button
            leftIcon={<IconPlus size={14} />}
            onClick={handleAdd}
            variant="light"
            size="xs"
          >
            {t("Add Relationship")}
          </Button>
        )}
      </Stack>
    </Center>
  );

  // Render relationship table
  const renderTable = () => (
    <Table highlightOnHover>
      <thead>
        <tr>
          <th>{t("Client Code")}</th>
          <th>{t("Client Name")}</th>
          <th>{t("Relationship")}</th>
          <th>{t("Remark")}</th>
          {canEdit && <th style={{ width: 100 }}>{t("Actions")}</th>}
        </tr>
      </thead>
      <tbody>
        {relationships.map((rel) => (
          <tr key={rel._id + (rel.isInverse ? "-inv" : "")}>
            <td>
              <UnstyledButton
                onClick={() => handleClientClick(rel.relatedClient)}
              >
                <Text size="sm" color="blue" sx={{ cursor: "pointer" }}>
                  {rel.relatedClient?.code || "-"}
                </Text>
              </UnstyledButton>
            </td>
            <td>
              <Stack spacing={0}>
                <Text size="sm">{rel.relatedClient?.name || "-"}</Text>
                {rel.relatedClient?.cname && (
                  <Text size="xs" color="dimmed">
                    {rel.relatedClient.cname}
                  </Text>
                )}
              </Stack>
            </td>
            <td>
              <Group spacing="xs">
                <Badge
                  size="sm"
                  color={getBadgeColor(
                    rel.relationshipType,
                    relationshipOptions,
                  )}
                >
                  {t(rel.relationshipType)}
                </Badge>
                {rel.isInverse && (
                  <Badge size="xs" variant="outline" color="gray">
                    {t("Auto")}
                  </Badge>
                )}
              </Group>
            </td>
            <td>
              <Text size="sm" color="dimmed" lineClamp={1}>
                {rel.remark || "-"}
              </Text>
            </td>
            {canEdit && (
              <td>
                <Group spacing="xs">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={() => handleEdit(rel)}
                    title={t("Edit")}
                  >
                    <IconPencil size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={() => handleDeleteClick(rel)}
                    title={t("Delete")}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <>
      <Paper p="md">
        {/* Header with Add button */}
        {canEdit && relationships.length > 0 && (
          <Group position="right" mb="md">
            <Button
              leftIcon={<IconPlus size={14} />}
              onClick={handleAdd}
              size="xs"
              // compact
            >
              {t("Add Relationship")}
            </Button>
          </Group>
        )}

        {/* Relationships list or empty state */}
        {relationships.length === 0 ? renderEmptyState() : renderTable()}
      </Paper>

      {/* Add/Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          editingRelationship
            ? t("Edit Client Relationship")
            : t("Add Client Relationship")
        }
        size="lg"
      >
        <Stack spacing="md">
          {/* Related Client Selection - only show when adding */}
          {!editingRelationship && (
            <RemoteSelect2
              label={t("Related Client")}
              apiEntity="client"
              valueField="_id"
              labelRender={(data) =>
                `[${data.code}] ${data.name}${data.cname ? ` (${data.cname})` : ""}`
              }
              searchFields={["code", "name", "cname"]}
              placeholder={t("Search by code or name...")}
              preQuery={{ _id: { $ne: clientId } }}
              onDataChange={handleRelatedClientChange}
              required
            />
          )}

          {/* Show selected client info when editing */}
          {editingRelationship && (
            <Paper p="sm" withBorder>
              <Group>
                <Text size="sm" weight={500}>
                  {t("Related Client")}:
                </Text>
                <Text size="sm">
                  [{editingRelationship.relatedClient?.code}]{" "}
                  {editingRelationship.relatedClient?.name}
                </Text>
              </Group>
            </Paper>
          )}

          {/* Relationship Type Selection */}
          <Select
            label={t("Relationship Type")}
            placeholder={t("Select relationship type")}
            data={formatOptions(relationshipOptions)}
            value={formData.relationshipType}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, relationshipType: value }))
            }
            searchable
            required
          />

          {/* Remark */}
          <Textarea
            label={t("Remark")}
            placeholder={t("Additional notes (optional)")}
            value={formData.remark}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, remark: e.target.value }))
            }
            minRows={2}
          />

          {/* Info message */}
          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            {t(
              "When you add this relationship, it will also appear on the related client's page with the inverse relationship type.",
            )}
          </Alert>

          {/* Actions */}
          <Group position="right">
            <Button variant="light" onClick={() => setModalOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              {t("Save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title={t("Confirm Delete")}
        size="sm"
      >
        <Stack spacing="md">
          <Text>{t("Are you sure you want to delete this relationship?")}</Text>
          {deletingRelationship && (
            <Paper p="sm" withBorder>
              <Text size="sm">
                {deletingRelationship.relatedClient?.name} -{" "}
                {t(deletingRelationship.relationshipType)}
              </Text>
            </Paper>
          )}
          <Alert icon={<IconAlertCircle size={16} />} color="orange">
            {t(
              "This will remove the relationship from both clients. This action cannot be undone.",
            )}
          </Alert>
          <Group position="right">
            <Button variant="light" onClick={() => setDeleteConfirmOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button color="red" onClick={handleDelete} loading={submitting}>
              {t("Delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default ClientRelationshipList;
